import React, { useState, useRef, useEffect } from 'react';
import PayrollImporter from '../utils/PayrollImporter';
import ExcelPayrollProcessor from '../utils/ExcelPayrollProcessor';
import EmployeeMappingModal from './EmployeeMappingModal';
import PaginatedPayrollPreview from './PaginatedPayrollPreview';
import { supabase } from '../supabaseClient';

const PayrollImportModalEnhanced = ({ isOpen, onClose, onImportComplete }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileType, setFileType] = useState(null);
  const [company, setCompany] = useState('KF');
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [availablePeriods, setAvailablePeriods] = useState([]);
  const [isImporting, setIsImporting] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [validationData, setValidationData] = useState(null);
  const [validationResolver, setValidationResolver] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const fileInputRef = useRef(null);

  // Fetch available payroll periods
  useEffect(() => {
    const fetchPayrollPeriods = async () => {
      try {
        const { data: periods, error } = await supabase
          .from('payroll_period')
          .select('id, payroll_number, period_label, start_date, end_date, pay_date, status')
          .order('payroll_number', { ascending: false });

        if (error) {
          console.error('Error fetching payroll periods:', error);
          return;
        }

        setAvailablePeriods(periods || []);
        
        // Auto-select the first active period
        const activePeriod = periods.find(p => p.status === 'active');
        if (activePeriod) {
          setSelectedPeriod(activePeriod.payroll_number.toString());
        }
      } catch (error) {
        console.error('Error fetching payroll periods:', error);
      }
    };

    if (isOpen) {
      fetchPayrollPeriods();
    }
  }, [isOpen]);

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const fileName = file.name.toLowerCase();
      
      if (fileName.endsWith('.csv')) {
        setFileType('csv');
        setSelectedFile(file);
        setError(null);
        setResult(null);
        setPreviewData(null);
      } else if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
        setFileType('excel');
        setSelectedFile(file);
        setError(null);
        setResult(null);
        
        // Process Excel file for preview
        await processExcelForPreview(file);
      } else {
        setError('Please select a CSV or Excel (.xlsx, .xls) file');
        return;
      }
    }
  };

  const processExcelForPreview = async (file) => {
    setIsProcessing(true);
    setError(null);
    
    try {
      const processor = new ExcelPayrollProcessor();
      
      processor.setPreviewCallback((data) => {
        setPreviewData(data);
        setShowPreview(true);
      });
      
      processor.setErrorCallback((errorData) => {
        setError(errorData.error);
      });
      
      await processor.processExcelFile(file, company);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) {
      setError('Please select a file first');
      return;
    }

    if (!selectedPeriod) {
      setError('Please select a payroll period first');
      return;
    }

    setIsImporting(true);
    setProgress(null);
    setResult(null);
    setError(null);
    setShowPreview(false);

    if (fileType === 'csv') {
      await handleCSVImport();
    } else if (fileType === 'excel') {
      await handleExcelImport();
    }
  };

  const handleCSVImport = async () => {
    const importer = new PayrollImporter();

    // Set up callbacks
    importer.setProgressCallback((progressData) => {
      setProgress(progressData);
    });

    importer.setCompleteCallback((resultData) => {
      setResult(resultData);
      setIsImporting(false);
      if (onImportComplete) {
        onImportComplete(resultData);
      }
    });

    importer.setErrorCallback((errorData) => {
      setError(errorData.error);
      setIsImporting(false);
    });

    importer.setValidationCallback((validationInfo) => {
      return new Promise((resolve) => {
        setValidationData(validationInfo);
        setValidationResolver(() => resolve);
        setShowMappingModal(true);
      });
    });

    try {
      await importer.importCSV(selectedFile, company, parseInt(selectedPeriod));
    } catch (err) {
      setError(err.message);
      setIsImporting(false);
    }
  };

  const handleExcelImport = async () => {
    if (!previewData) {
      setError('Please wait for Excel file to be processed');
      setIsImporting(false);
      return;
    }

    const processor = new ExcelPayrollProcessor();
    
    processor.setProgressCallback((progressData) => {
      setProgress(progressData);
    });

    processor.setCompleteCallback((resultData) => {
      setResult(resultData);
      setIsImporting(false);
      if (onImportComplete) {
        onImportComplete(resultData);
      }
    });

    processor.setErrorCallback((errorData) => {
      setError(errorData.error);
      setIsImporting(false);
    });

    try {
      // Check if preview data exists
      if (!previewData || !previewData.payrollRecords) {
        throw new Error('No preview data available. Please select a file first.');
      }
      
      // Validate employees
      const validationResult = await processor.validateAndMapEmployees(previewData.payrollRecords);
      
      if (validationResult.unmatchedRecords.length > 0) {
        // Show mapping modal for unmatched employees
        const mappingResult = await new Promise((resolve) => {
          setValidationData({
            unmatchedRecords: validationResult.unmatchedRecords,
            validRecords: validationResult.validRecords,
            company: company
          });
          setValidationResolver(() => resolve);
          setShowMappingModal(true);
        });
        
        if (mappingResult.cancelled) {
          setIsImporting(false);
          return;
        }
        
        // Update records with mappings
        // TODO: Implement mapping logic
      }
      
      // Import to database
      await processor.importToDatabase(
        previewData.payrollRecords || [],
        previewData.attendanceRecords || [],
        company,
        parseInt(selectedPeriod)
      );
    } catch (err) {
      console.error('Excel import error:', err);
      setError(err.message || 'Failed to import Excel file');
      setIsImporting(false);
    }
  };

  const handleMappingConfirm = (mappingResult) => {
    setShowMappingModal(false);
    if (validationResolver) {
      validationResolver(mappingResult);
      setValidationResolver(null);
    }
    setValidationData(null);
  };

  const handleMappingCancel = () => {
    setShowMappingModal(false);
    if (validationResolver) {
      validationResolver({ cancelled: true });
      setValidationResolver(null);
    }
    setValidationData(null);
    setIsImporting(false);
  };

  const handleClose = () => {
    if (!isImporting && !isProcessing) {
      setSelectedFile(null);
      setFileType(null);
      setSelectedPeriod('');
      setProgress(null);
      setResult(null);
      setError(null);
      setShowMappingModal(false);
      setValidationData(null);
      setPreviewData(null);
      setShowPreview(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      onClose();
    }
  };

  const getProgressMessage = () => {
    if (!progress) return '';
    
    if (progress.stage) {
      return progress.message;
    }
    
    if (progress.percentage !== undefined) {
      return `Importing: ${progress.processed}/${progress.total} records (${progress.percentage}%)`;
    }
    
    return 'Processing...';
  };

  const handleCompanyChange = async (e) => {
    const newCompany = e.target.value;
    setCompany(newCompany);
    
    // Re-process Excel file if already selected
    if (selectedFile && fileType === 'excel') {
      await processExcelForPreview(selectedFile);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-800">Import Payroll Data</h2>
          <button
            onClick={handleClose}
            disabled={isImporting || isProcessing}
            className="text-gray-500 hover:text-gray-700 disabled:opacity-50"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Company Selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Company
          </label>
          <select
            value={company}
            onChange={handleCompanyChange}
            disabled={isImporting || isProcessing}
            className="w-full p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
          >
            <option value="KF">KF</option>
            <option value="KOO">KOO</option>
          </select>
        </div>

        {/* Payroll Period Selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Payroll Period
          </label>
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            disabled={isImporting || isProcessing}
            className="w-full p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
          >
            <option value="">Select a payroll period...</option>
            {availablePeriods.map((period) => (
              <option key={period.payroll_number} value={period.payroll_number}>
                {period.period_label} (#{period.payroll_number}) - {period.status}
              </option>
            ))}
          </select>
          {selectedPeriod && (
            <p className="text-sm text-gray-600 mt-1">
              Selected: {availablePeriods.find(p => p.payroll_number.toString() === selectedPeriod)?.period_label}
            </p>
          )}
        </div>

        {/* File Selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            CSV or Excel File
          </label>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={handleFileSelect}
            disabled={isImporting || isProcessing}
            className="w-full p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
          />
          {selectedFile && (
            <p className="text-sm text-gray-600 mt-1">
              Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
              {fileType === 'excel' && ' - Excel format detected'}
            </p>
          )}
        </div>

        {/* Processing indicator */}
        {isProcessing && (
          <div className="mb-4 bg-blue-100 border border-blue-300 rounded p-3">
            <p className="text-sm text-blue-800">Processing Excel file...</p>
          </div>
        )}

        {/* Preview Section */}
        {showPreview && previewData && !isImporting && (
          <div className="mb-4 border border-gray-300 rounded p-4">
            <h3 className="font-semibold text-lg mb-3">Data Preview</h3>
            
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div className="bg-gray-50 p-3 rounded">
                <p className="text-sm font-medium text-gray-700">Payroll Period</p>
                <p className="text-sm text-gray-600">
                  {previewData.summary.periodStart} to {previewData.summary.periodEnd}
                </p>
              </div>
              <div className="bg-gray-50 p-3 rounded">
                <p className="text-sm font-medium text-gray-700">Total Employees</p>
                <p className="text-sm text-gray-600">{previewData.summary.totalEmployees}</p>
              </div>
            </div>

            {/* Complete Data Preview with Pagination */}
            <div className="mb-3">
              <p className="text-sm font-medium text-gray-700 mb-3">
                Complete Data for Manual Review:
              </p>
              <PaginatedPayrollPreview 
                payrollRecords={previewData.payrollRecords}
                attendanceRecords={previewData.attendanceRecords}
              />
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div className="bg-green-50 p-2 rounded">
                <p className="font-medium text-green-800">Payroll Records</p>
                <p className="text-green-600">{previewData.summary.totalEmployees}</p>
              </div>
              <div className="bg-blue-50 p-2 rounded">
                <p className="font-medium text-blue-800">Attendance Records</p>
                <p className="text-blue-600">{previewData.summary.totalAttendanceRecords}</p>
              </div>
              <div className="bg-yellow-50 p-2 rounded">
                <p className="font-medium text-yellow-800">Skipped (0 hours)</p>
                <p className="text-yellow-600">{previewData.summary.totalSkipped}</p>
              </div>
            </div>

            {/* Skipped Employees */}
            {previewData.skippedEmployees.length > 0 && (
              <div className="mt-3">
                <p className="text-xs font-medium text-gray-700">Skipped Employees:</p>
                <ul className="text-xs text-gray-600 mt-1">
                  {previewData.skippedEmployees.slice(0, 3).map((emp, idx) => (
                    <li key={idx}>• {emp.name} - {emp.reason}</li>
                  ))}
                  {previewData.skippedEmployees.length > 3 && (
                    <li>... and {previewData.skippedEmployees.length - 3} more</li>
                  )}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Progress */}
        {isImporting && progress && (
          <div className="mb-4">
            <div className="bg-blue-100 border border-blue-300 rounded p-3">
              <p className="text-sm text-blue-800">{getProgressMessage()}</p>
              {progress.percentage !== undefined && (
                <div className="mt-2 bg-blue-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${progress.percentage}%` }}
                  ></div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="mb-4 bg-red-100 border border-red-300 rounded p-3">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Success Result */}
        {result && result.success && (
          <div className="mb-4 bg-green-100 border border-green-300 rounded p-3">
            <p className="text-sm text-green-800 font-medium">Import Successful!</p>
            <p className="text-sm text-green-700 mt-1">{result.message}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3">
          <button
            onClick={handleClose}
            disabled={isImporting || isProcessing}
            className="px-4 py-2 text-gray-700 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50 transition-colors"
          >
            {result ? 'Close' : 'Cancel'}
          </button>
          <button
            onClick={handleImport}
            disabled={!selectedFile || isImporting || isProcessing || !(!showPreview || (showPreview && previewData))}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isImporting ? 'Importing...' : 'Import'}
          </button>
        </div>

        {/* Instructions */}
        <div className="mt-4 text-xs text-gray-500">
          <p><strong>Instructions:</strong></p>
          <ul className="list-disc list-inside mt-1 space-y-1">
            <li><strong>Payroll Excel Files:</strong> Select Excel file with "Payroll New" sheet (contains both KF and KOO data)</li>
            <li><strong>CSV Files:</strong> For payroll dynamics deduction reports, select separate CSV files for each company</li>
            <li>Excel files will show a preview before importing</li>
            <li>The system will filter data based on the selected company above</li>
            <li>Employees with 0 hours and 0 pay will be automatically excluded</li>
            <li>Existing data for the selected company will be replaced</li>
          </ul>
        </div>

        {/* Employee Mapping Modal */}
        {validationData && (
          <EmployeeMappingModal
            isOpen={showMappingModal}
            unmatchedRecords={validationData.unmatchedRecords}
            validRecords={validationData.validRecords}
            company={validationData.company}
            onConfirm={handleMappingConfirm}
            onCancel={handleMappingCancel}
          />
        )}
      </div>
    </div>
  );
};

export default PayrollImportModalEnhanced;